import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export type HomeRole = 'owner' | 'manager' | 'employee';

export type Quote = {
  text: string;
  name: string;
  role: string;
  stars: string;
};

type HeroDelta = { dx: number; dy: number };

const WORDS_CS = ['nábor', 'směny', 'docházka', 'smlouvy'];
const WORDS_EN = ['hiring', 'shifts', 'attendance', 'contracts'];

const QUOTES_TOP_CS: Quote[] = [
  {
    text: 'Rozvrh na příští týden mám hotový za deset minut. Dřív mi to sežralo celý čtvrtek.',
    name: 'Martina Dvořáková',
    role: 'Majitelka, Kavárna Vlna',
    stars: '★★★★★',
  },
  {
    text: 'Docházka sedí na minuty a účetní dostane podklady exportem. Konec ručního přepisování.',
    name: 'Tomáš Ryba',
    role: 'Provozní ředitel, Logistix',
    stars: '★★★★★',
  },
  {
    text: 'Brigádníky naberu a podepíšu smlouvu online. Nástup do dvou dnů místo dvou týdnů.',
    name: 'Klára Bílá',
    role: 'HR manažerka, Retail Group',
    stars: '★★★★★',
  },
  {
    text: 'Lidem se líbí, že si směny vyměňují sami v aplikaci. Telefonáty v neděli večer skončily.',
    name: 'Jan Sedláček',
    role: 'Majitel, Bistro 21',
    stars: '★★★★☆',
  },
];

const QUOTES_BOTTOM_CS: Quote[] = [
  {
    text: 'Přehled o nákladech na směny mám v reálném čase. Konečně vím, kde nám utíkají peníze.',
    name: 'Petra Nováková',
    role: 'Finanční ředitelka, Gastro Plus',
    stars: '★★★★★',
  },
  {
    text: 'Šest poboček, jeden rozvrh. Zaskakování mezi provozovnami vyřešíme na pár kliků.',
    name: 'Michal Horák',
    role: 'Regionální manažer, FreshMarket',
    stars: '★★★★★',
  },
  {
    text: 'Nasazení trvalo jedno odpoledne. Tým to pobral bez školení, aplikace je jasná.',
    name: 'Lucie Kratochvílová',
    role: 'Vedoucí provozu, Hotel Aurora',
    stars: '★★★★★',
  },
  {
    text: 'Schvalování dovolených už neřeším mailem. Všechno je na jednom místě i s historií.',
    name: 'Ondřej Malý',
    role: 'HR business partner, Nordic Steel',
    stars: '★★★★☆',
  },
];

const QUOTES_TOP_EN: Quote[] = [
  {
    text: 'Next week’s schedule takes me ten minutes. It used to eat my whole Thursday.',
    name: 'Martina Dvořáková',
    role: 'Owner, Kavárna Vlna',
    stars: '★★★★★',
  },
  {
    text: 'Attendance is accurate to the minute and payroll gets an export. No more manual transcription.',
    name: 'Tomáš Ryba',
    role: 'Operations Director, Logistix',
    stars: '★★★★★',
  },
  {
    text: 'I hire seasonal staff and sign contracts online. Onboarding in two days instead of two weeks.',
    name: 'Klára Bílá',
    role: 'HR Manager, Retail Group',
    stars: '★★★★★',
  },
  {
    text: 'People love swapping shifts in the app. Sunday-night phone calls are gone.',
    name: 'Jan Sedláček',
    role: 'Owner, Bistro 21',
    stars: '★★★★☆',
  },
];

const QUOTES_BOTTOM_EN: Quote[] = [
  {
    text: 'I see shift costs in real time. Finally I know where money leaks.',
    name: 'Petra Nováková',
    role: 'CFO, Gastro Plus',
    stars: '★★★★★',
  },
  {
    text: 'Six locations, one schedule. Covering between sites takes a few clicks.',
    name: 'Michal Horák',
    role: 'Regional Manager, FreshMarket',
    stars: '★★★★★',
  },
  {
    text: 'Rollout took one afternoon. The team got it without training - the app is clear.',
    name: 'Lucie Kratochvílová',
    role: 'Ops Lead, Hotel Aurora',
    stars: '★★★★★',
  },
  {
    text: 'Leave approvals no longer live in email. Everything is in one place with history.',
    name: 'Ondřej Malý',
    role: 'HR Business Partner, Nordic Steel',
    stars: '★★★★☆',
  },
];

function pill(on: boolean) {
  return { bg: on ? '#0065F4' : 'transparent', fg: on ? '#FFFFFF' : '#6B7280' };
}

export function useHomeLanding(lang: 'cs' | 'en') {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [role, setRole] = useState<HomeRole>('owner');
  const [mx, setMx] = useState(0);
  const [my, setMy] = useState(0);
  const [chart, setChart] = useState(false);
  const [, setTourStep] = useState(0);
  const [t, setT] = useState(0);
  const [yearly, setYearly] = useState(false);
  const [revP, setRevP] = useState(0.5);
  const [typed, setTyped] = useState(WORDS_CS[0]);
  const [heroP, setHeroP] = useState(0);
  const [heroD, setHeroD] = useState<HeroDelta[] | null>(null);
  const [stackMobile, setStackMobile] = useState(false);

  const heroRef = useRef<HTMLDivElement>(null);
  const hc1 = useRef<HTMLDivElement>(null);
  const hc2 = useRef<HTMLDivElement>(null);
  const hc3 = useRef<HTMLDivElement>(null);
  const hc4 = useRef<HTMLDivElement>(null);
  const hcRefs = useRef([hc1, hc2, hc3, hc4]).current;
  const tourRef = useRef<HTMLElement>(null);
  const statsRef = useRef<HTMLElement>(null);
  const revRef = useRef<HTMLElement>(null);
  const roleRef = useRef<HTMLElement>(null);
  const countedRef = useRef(false);
  const syncStackOffsetsRef = useRef<() => void>(() => {});
  const stackMetricsRef = useRef({
    peek: 64,
    cardH: 440,
    stageH: 440,
    pinTop: 106,
    range: 1,
  });

  const words = lang === 'cs' ? WORDS_CS : WORDS_EN;
  const quotesTop = lang === 'cs' ? QUOTES_TOP_CS : QUOTES_TOP_EN;
  const quotesBottom = lang === 'cs' ? QUOTES_BOTTOM_CS : QUOTES_BOTTOM_EN;

  const goRole = useCallback((frac: number) => {
    const rl = roleRef.current;
    if (!rl) return;
    const rb = rl.getBoundingClientRect();
    const top = window.scrollY + rb.top + frac * Math.max(rb.height - window.innerHeight, 1);
    window.scrollTo({ top, behavior: 'smooth' });
  }, []);

  const runCount = useCallback(() => {
    countedRef.current = true;
    const start = performance.now();
    const dur = 1400;
    const id = window.setInterval(() => {
      const p = Math.min((performance.now() - start) / dur, 1);
      setT(1 - Math.pow(1 - p, 3));
      if (p >= 1) clearInterval(id);
    }, 32);
  }, []);

  useEffect(() => {
    const measureHero = () => {
      const c = heroRef.current;
      if (!c) {
        requestAnimationFrame(measureHero);
        return;
      }
      const cx = c.offsetWidth / 2;
      const cy = c.offsetHeight / 2;
      const d = hcRefs.map((r) => {
        const el = r.current;
        if (!el) return { dx: 0, dy: 0 };
        const rx = cx - (el.offsetLeft + el.offsetWidth / 2);
        const ry = cy - (el.offsetTop + el.offsetHeight / 2);
        const L = (Math.abs(rx) + Math.abs(ry)) * 0.78;
        return {
          dx: Math.round(Math.sign(rx || 1) * L),
          dy: Math.round(Math.sign(ry || 1) * L),
        };
      });
      setHeroD(d);
    };
    requestAnimationFrame(measureHero);

    const mqStack = window.matchMedia('(max-width: 900px)');
    const syncStackMobile = () => setStackMobile(mqStack.matches);
    syncStackMobile();
    mqStack.addEventListener('change', syncStackMobile);

    const onResize = () => measureHero();
    window.addEventListener('resize', onResize);

    const clearMobileStackStyles = (
      section: HTMLElement,
      cardsWrap: HTMLElement,
      stage: HTMLElement | null,
      cards: HTMLElement[]
    ) => {
      section.style.removeProperty('min-height');
      cardsWrap.style.removeProperty('height');
      cardsWrap.style.removeProperty('min-height');
      stage?.style.removeProperty('height');
      document.documentElement.style.removeProperty('--stackRunwayH');
      document.documentElement.style.removeProperty('--stackStageH');
      document.documentElement.style.removeProperty('--stackCard1Top');
      document.documentElement.style.removeProperty('--introH');
      document.documentElement.style.removeProperty('--stackPeek');
      cards.forEach((card) => {
        card.style.removeProperty('--stackCardTop');
        card.style.removeProperty('--stackCardGap');
        card.style.removeProperty('--stackY');
      });
    };

    const updateStackProgress = () => {
      if (!window.matchMedia('(max-width: 900px)').matches) return;
      const cardsWrap = document.querySelector('[data-stack-cards]') as HTMLElement | null;
      const cards = Array.from(
        document.querySelectorAll('[data-stack-card]')
      ) as HTMLElement[];
      if (!cardsWrap || cards.length < 3) return;

      const { peek, stageH, pinTop, range } = stackMetricsRef.current;
      // Progress while the shared pin (intro + cards) is stuck
      const raw = pinTop - cardsWrap.getBoundingClientRect().top;
      const p = Math.min(1, Math.max(0, raw / Math.max(range, 1)));

      cards.forEach((card, i) => {
        let y = 0;
        if (i === 0) {
          y = 0;
        } else {
          const startP = (i - 1) / 2;
          const endP = i / 2;
          const t = Math.min(1, Math.max(0, (p - startP) / Math.max(endP - startP, 0.0001)));
          const from = i === 1 ? stageH : stageH + 54;
          const to = i * peek;
          y = from + (to - from) * t;
        }
        card.style.setProperty('--stackY', `${Math.round(y)}px`);
      });
    };

    const syncStackOffsets = () => {
      const header = document.querySelector('[data-header]') as HTMLElement | null;
      const section = document.getElementById('tour') as HTMLElement | null;
      const intro = document.querySelector('[data-stack-intro]') as HTMLElement | null;
      const cardsWrap = document.querySelector('[data-stack-cards]') as HTMLElement | null;
      const stage = document.querySelector('[data-stack-stage]') as HTMLElement | null;
      const cards = Array.from(
        document.querySelectorAll('[data-stack-card]')
      ) as HTMLElement[];

      const mobile = window.matchMedia('(max-width: 900px)').matches;

      if (!section || !intro || !cardsWrap || cards.length < 3) return;

      if (!mobile) {
        clearMobileStackStyles(section, cardsWrap, stage, cards);
        return;
      }

      const headerH = header
        ? Math.ceil(header.getBoundingClientRect().height + 10)
        : 106;
      document.documentElement.style.setProperty('--stackHeaderH', `${headerH}px`);

      const introH = intro.offsetHeight;
      document.documentElement.style.setProperty('--introH', `${introH}px`);

      let peek = 64;
      const num0 = cards[0].querySelector('[data-stack-num]') as HTMLElement | null;
      if (num0) {
        const padTop = parseFloat(getComputedStyle(cards[0]).paddingTop) || 24;
        const fontSize = parseFloat(getComputedStyle(num0).fontSize) || 56;
        const numTop = parseFloat(getComputedStyle(num0).top) || -10;
        peek = Math.ceil(padTop + numTop + fontSize + 2);
        peek = Math.min(110, Math.max(44, peek));
      }
      document.documentElement.style.setProperty('--stackPeek', `${peek}px`);

      cards.forEach((card) => card.style.setProperty('--stackY', '0px'));
      const cardH = Math.max(...cards.map((c) => c.offsetHeight), 380);
      const stageH = cardH + 2 * peek;
      const pinH = introH + 12 + stageH;
      const stepScroll = Math.min(420, Math.max(260, Math.round(window.innerHeight * 0.48)));
      const runwayH = pinH + stepScroll * 2;
      const pinTop = headerH;
      const range = Math.max(runwayH - pinH, 1);

      stackMetricsRef.current = { peek, cardH, stageH, pinTop, range };

      document.documentElement.style.setProperty('--stackStageH', `${stageH}px`);
      document.documentElement.style.setProperty('--stackRunwayH', `${runwayH}px`);
      cardsWrap.style.height = `${runwayH}px`;

      const cs = getComputedStyle(section);
      const padY =
        (parseFloat(cs.paddingTop) || 0) + (parseFloat(cs.paddingBottom) || 0);
      section.style.minHeight = `${Math.ceil(runwayH + padY)}px`;

      updateStackProgress();
    };

    syncStackOffsetsRef.current = syncStackOffsets;
    window.addEventListener('resize', syncStackOffsets, { passive: true });
    syncStackOffsets();
    const syncT = window.setTimeout(syncStackOffsets, 400);
    const stackRo =
      typeof ResizeObserver !== 'undefined'
        ? new ResizeObserver(() => syncStackOffsets())
        : null;
    const introEl = document.querySelector('[data-stack-intro]');
    if (introEl && stackRo) stackRo.observe(introEl);
    document.querySelectorAll('[data-stack-card]').forEach((el) => {
      if (stackRo) stackRo.observe(el);
    });

    ['/home/mac-base.png', '/home/mac-lid.png', '/home/hero-card-3.png', '/home/mobile-shifts.png'].forEach((src) => {
      const i = new Image();
      i.src = src;
    });

    const onScroll = () => {
      setScrolled(window.scrollY > 12);
      updateStackProgress();
      const el = tourRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        const p = (window.innerHeight * 0.55 - r.top) / Math.max(r.height, 1);
        setTourStep(p < 0.3 ? 0 : p < 0.62 ? 1 : 2);
      }
      const rev = revRef.current;
      if (rev) {
        const rr = rev.getBoundingClientRect();
        const raw = (window.innerHeight - rr.top) / (window.innerHeight + rr.height);
        setRevP(Math.round(Math.min(1, Math.max(0, raw)) * 1000) / 1000);
      }
      const rl = roleRef.current;
      if (rl) {
        const rb = rl.getBoundingClientRect();
        const span = Math.max(rb.height - window.innerHeight, 1);
        const rp = Math.min(1, Math.max(0, -rb.top / span));
        setRole(rp < 0.34 ? 'owner' : rp < 0.68 ? 'manager' : 'employee');
      }
      const hero = heroRef.current;
      if (hero) {
        const hr = hero.getBoundingClientRect();
        const gap = hr.bottom - window.innerHeight;
        const range = Math.max(Math.min(hr.height, window.innerHeight * 0.55), 1);
        setHeroP(Math.round(Math.min(1, Math.max(0, 1 - gap / range)) * 1000) / 1000);
      }
      const s = statsRef.current;
      if (s && !countedRef.current && s.getBoundingClientRect().top < window.innerHeight * 0.85) {
        countedRef.current = true;
        runCount();
      }
    };

    const onMove = (e: MouseEvent) => {
      setMx((e.clientX / window.innerWidth - 0.5) * 2);
      setMy((e.clientY / window.innerHeight - 0.5) * 2);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('mousemove', onMove, { passive: true });
    onScroll();

    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('resize', syncStackOffsets);
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('mousemove', onMove);
      mqStack.removeEventListener('change', syncStackMobile);
      clearTimeout(syncT);
      stackRo?.disconnect();
    };
    // hcRefs is a stable array of refs created once
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runCount]);

  // Re-measure after switching mobile/desktop tour DOM
  useEffect(() => {
    const t = window.setTimeout(() => syncStackOffsetsRef.current(), 50);
    return () => clearTimeout(t);
  }, [stackMobile]);

  // Re-measure stack offsets while the floating header animates height
  useEffect(() => {
    let raf = 0;
    const end = performance.now() + 320;
    const tick = () => {
      syncStackOffsetsRef.current();
      if (performance.now() < end) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [scrolled]);

  // Typewriter
  useEffect(() => {
    const reduced =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setTyped(words[0]);
      return;
    }
    let wi = 0;
    let ci = words[0].length;
    let del = false;
    let timer: number;
    setTyped(words[0]);

    const tick = () => {
      const w = words[wi];
      let delay: number;
      if (del) {
        ci -= 1;
        delay = 45 + Math.random() * 25;
        if (ci <= 0) {
          del = false;
          wi = (wi + 1) % words.length;
          delay = 260;
        }
      } else if (ci >= w.length) {
        del = true;
        delay = 1900;
      } else {
        ci += 1;
        delay = 85 + Math.random() * 60;
      }
      setTyped(words[wi].slice(0, ci));
      timer = window.setTimeout(tick, delay);
    };
    timer = window.setTimeout(tick, 1800);
    return () => clearTimeout(timer);
  }, [words]);

  const vals = useMemo(() => {
    const reduce =
      typeof window !== 'undefined' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const rmx = reduce ? 0 : mx;
    const rmy = reduce ? 0 : my;
    const ease = (x: number) => 1 - Math.pow(1 - x, 3);
    const hp = reduce ? 1 : ease(heroP);
    const flc = (k: number, i: number) => {
      const d = (heroD && heroD[i]) || { dx: 0, dy: 0 };
      const g = 1 - hp;
      return `translate3d(${(rmx * k + d.dx * g).toFixed(1)}px, ${(rmy * k + d.dy * g).toFixed(1)}px, 0) scale(${(0.62 + 0.38 * hp).toFixed(3)})`;
    };
    const o = pill(role === 'owner');
    const m = pill(role === 'manager');
    const e = pill(role === 'employee');
    const cz = pill(lang === 'cs');
    const en = pill(lang === 'en');
    const mo = pill(!yearly);
    const yr = pill(yearly);
    const bars = chart ? ['40%', '70%', '52%', '88%', '64%'] : ['12%', '12%', '12%', '12%', '12%'];
    const nfmt = (n: number) =>
      Math.round(n)
        .toLocaleString('cs-CZ')
        .replace(/\u00a0/g, ' ');
    const p = revP;
    const top = quotesTop.concat(quotesTop).concat(quotesTop);
    const bottom = quotesBottom.concat(quotesBottom).concat(quotesBottom);

    return {
      menuOpen,
      menuOpen01: menuOpen ? '1' : '0',
      toggleMenu: () => setMenuOpen((v) => !v),
      closeMenu: () => setMenuOpen(false),
      navBg: scrolled ? 'rgba(255,255,255,0.85)' : 'transparent',
      navTop: scrolled ? '32px' : '0px',
      navRadius: scrolled ? '999px' : '0px',
      navMax: scrolled ? '1120px' : '1200px',
      navShadow: scrolled
        ? '0 1px 0 rgba(17,24,39,0.06), 0 8px 24px rgba(17,24,39,0.05)'
        : 'none',
      navBlur: scrolled ? 'saturate(180%) blur(12px)' : 'none',
      czBg: cz.bg,
      czColor: cz.fg,
      enBg: en.bg,
      enColor: en.fg,
      langLabel: lang === 'cs' ? 'Čeština' : 'English',
      heroRef,
      hc1,
      hc2,
      hc3,
      hc4,
      heroCardOp: heroD ? (0.15 + 0.85 * hp).toFixed(3) : '0',
      heroCardTrans: heroD
        ? 'transform 220ms cubic-bezier(0.22,0.61,0.36,1), opacity 220ms ease-out'
        : 'none',
      float1: flc(-18, 0),
      float2: flc(-26, 1),
      float3: flc(22, 2),
      float4: flc(-14, 3),
      typed,
      reduced: reduce,
      roleRef,
      setOwner: () => goRole(0.12),
      setManager: () => goRole(0.5),
      setEmployee: () => goRole(0.88),
      ownerOpacity: role === 'owner' ? 1 : 0,
      managerOpacity: role === 'manager' ? 1 : 0,
      employeeOpacity: role === 'employee' ? 1 : 0,
      ownerShift: role === 'owner' ? 'translateY(0)' : 'translateY(10px)',
      managerShift: role === 'manager' ? 'translateY(0)' : 'translateY(10px)',
      employeeShift: role === 'employee' ? 'translateY(0)' : 'translateY(10px)',
      ownerPE: role === 'owner' ? 'auto' : 'none',
      managerPE: role === 'manager' ? 'auto' : 'none',
      employeePE: role === 'employee' ? 'auto' : 'none',
      dotOwnerW: role === 'owner' ? '24px' : '8px',
      dotManagerW: role === 'manager' ? '24px' : '8px',
      dotEmployeeW: role === 'employee' ? '24px' : '8px',
      dotOwner: role === 'owner' ? '#0065F4' : '#DFE4EC',
      dotManager: role === 'manager' ? '#0065F4' : '#DFE4EC',
      dotEmployee: role === 'employee' ? '#0065F4' : '#DFE4EC',
      ownerBg: o.bg,
      ownerColor: o.fg,
      managerBg: m.bg,
      managerColor: m.fg,
      employeeBg: e.bg,
      employeeColor: e.fg,
      showChart: () => setChart(true),
      hideChart: () => setChart(false),
      chartOpacity: chart ? 1 : 0.45,
      bar1: bars[0],
      bar2: bars[1],
      bar3: bars[2],
      bar4: bars[3],
      bar5: bars[4],
      tourRef,
      stackMobile,
      statsRef,
      revRef,
      stat1: `${nfmt(10000 * t)}+`,
      stat2: `${Math.round(98 * t)} %`,
      stat3: `${Math.round(5 * t)} min.`,
      stat4: String(Math.round(2 * t)),
      quotesTop: top,
      quotesBottom: bottom,
      rowAX: `translateX(${(-300 - p * 760).toFixed(1)}px)`,
      rowBX: `translateX(${(-1060 + p * 760).toFixed(1)}px)`,
      setMonthly: () => setYearly(false),
      setYearly: () => setYearly(true),
      monthlyBg: mo.bg,
      monthlyColor: mo.fg,
      yearlyBg: yr.bg,
      yearlyColor: yr.fg,
      priceGrowth: yearly ? '31' : '39',
      pricePro: yearly ? '55' : '69',
      priceBusiness: yearly ? '79' : '99',
    };
  }, [
    menuOpen,
    scrolled,
    lang,
    heroD,
    heroP,
    mx,
    my,
    typed,
    role,
    chart,
    t,
    revP,
    yearly,
    quotesTop,
    quotesBottom,
    goRole,
    hc1,
    hc2,
    hc3,
    hc4,
  ]);

  return vals;
}

export type HomeLandingVals = ReturnType<typeof useHomeLanding>;
